import {
  SONAR_MAX_RANGE_METERS,
  SONAR_MIN_RANGE_METERS,
  SONAR_RANGE_STEP_METERS,
  getSonarPulsePowerMw,
  getSonarScanCostMultiplier
} from '../../shared/constants.js';
import type { GameSnapshot, RoomState, SonarScanType } from '../../shared/types.js';
import { state as controllerState } from '../controllerState.js';
import { clamp, createCard } from '../controllerUiHelpers.js';
import { t } from '../i18n.js';
import { SonarScanView, type SonarPingHit } from '../rendering/SonarScanView.js';
import type { GraphicsQuality } from '../rendering/quality.js';

type CreateSonarRoomControllerOptions = {
  controls: HTMLDivElement | null;
  getGraphicsQuality: () => GraphicsQuality;
};

export const createSonarRoomController = ({
  controls,
  getGraphicsQuality
}: CreateSonarRoomControllerOptions) => {
  let sonarRangeMeters = 50;
  let sonarScanType: SonarScanType = 'terrain';
  let sonarRangeUpdateCallback: (() => void) | null = null;
  let sonarRangeLabelElement: HTMLElement | null = null;
  let sonarBatteryWarningElement: HTMLDivElement | null = null;
  let sonarScanView: SonarScanView | null = null;

  const updateBatteryWarning = (snapshot: GameSnapshot | null): void => {
    if (!sonarBatteryWarningElement) {
      return;
    }

    const batteryCharge = snapshot?.submarine.batteryCharge ?? 100;
    const pingPowerMw = getSonarPulsePowerMw(sonarRangeMeters) * getSonarScanCostMultiplier(sonarScanType);
    const showWarning = batteryCharge < pingPowerMw;

    sonarBatteryWarningElement.classList.toggle('visible', showWarning);
    sonarBatteryWarningElement.setAttribute('aria-hidden', showWarning ? 'false' : 'true');
  };

  const updateRangeInternal = (nextRangeMeters: number): void => {
    const clamped = clamp(nextRangeMeters, SONAR_MIN_RANGE_METERS, SONAR_MAX_RANGE_METERS);
    if (clamped === sonarRangeMeters) {
      return;
    }
    sonarRangeMeters = clamped;

    if (sonarRangeLabelElement) {
      sonarRangeLabelElement.textContent = `Range ${Math.round(sonarRangeMeters)} m`;
    }

    updateBatteryWarning(controllerState.lastSnapshot);
    sonarScanView?.setSonarRadiusMeters(sonarRangeMeters);
    sonarRangeUpdateCallback?.();
  };

  const incrementRange = (): void => {
    updateRangeInternal(sonarRangeMeters + SONAR_RANGE_STEP_METERS);
  };

  const decrementRange = (): void => {
    updateRangeInternal(sonarRangeMeters - SONAR_RANGE_STEP_METERS);
  };

  const render = (): void => {
    if (!controls) {
      return;
    }

    controls.classList.add('sonar-controls-layout');

    const scanCard = createCard('Sonar', 'Select scan type then press ping. Drag to orbit, mouse wheel or pinch to zoom.');
    scanCard.classList.add('sonar-control-card', 'ctrl-room-main-card');

    const sonarRangeLabel = document.createElement('span');
    sonarRangeLabel.className = 'sonar-range-label';
    sonarRangeLabelElement = sonarRangeLabel;

    sonarRangeMeters = clamp(
      Math.round(sonarRangeMeters / SONAR_RANGE_STEP_METERS) * SONAR_RANGE_STEP_METERS,
      SONAR_MIN_RANGE_METERS,
      SONAR_MAX_RANGE_METERS
    );

    sonarRangeLabel.textContent = `Range ${Math.round(sonarRangeMeters)} m`;

    const viewport = document.createElement('div');
    viewport.className = 'sonar-scan-viewport';

    const batteryWarning = document.createElement('div');
    batteryWarning.className = 'sonar-battery-warning';
    batteryWarning.textContent = t('battery_warning');
    batteryWarning.setAttribute('aria-hidden', 'true');
    sonarBatteryWarningElement = batteryWarning;

    const scanTypeOptions: Array<{ type: SonarScanType; label: string; caption: string }> = [
      { type: 'terrain', label: 'Terrain', caption: 'Maps nearest surface. No nav update. (×1 cost)' },
      { type: 'resource', label: 'Resource', caption: 'Shows resource clouds. Updates nav map. (×1.5 cost)' },
    ];

    const scanTypeRow = document.createElement('div');
    scanTypeRow.className = 'sonar-scan-type-row';

    const fadeCaption = document.createElement('p');
    fadeCaption.className = 'muted sonar-fade-caption';
    fadeCaption.textContent = scanTypeOptions.find((o) => o.type === sonarScanType)?.caption ?? '';

    const scanTypeButtons: HTMLButtonElement[] = [];
    for (const option of scanTypeOptions) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = option.label;
      btn.className = 'sonar-scan-type-btn' + (sonarScanType === option.type ? ' active' : '');
      btn.addEventListener('click', () => {
        sonarScanType = option.type;
        for (const b of scanTypeButtons) b.classList.remove('active');
        btn.classList.add('active');
        fadeCaption.textContent = option.caption;
        updateBatteryWarning(controllerState.lastSnapshot);
        sonarRangeUpdateCallback?.();
      });
      scanTypeButtons.push(btn);
      scanTypeRow.append(btn);
    }

    viewport.append(batteryWarning);
    scanCard.append(scanTypeRow, viewport, fadeCaption);
    controls.append(scanCard);

    sonarScanView?.dispose();
    sonarScanView = new SonarScanView(viewport, getGraphicsQuality());
    if (controllerState.lastRoomState) {
      sonarScanView.setRoomState(controllerState.lastRoomState);
    }
    if (controllerState.lastSnapshot) {
      sonarScanView.setSnapshot(controllerState.lastSnapshot);
    }
    sonarScanView.setSonarRadiusMeters(sonarRangeMeters);
    updateBatteryWarning(controllerState.lastSnapshot);
    updateDepthDial(controllerState.lastSnapshot);
  };

  const updateDepthDial = (snapshot: GameSnapshot | null): void => {
    if (!snapshot) {
      return;
    }

    sonarScanView?.setSnapshot(snapshot);
  };

  const setRoomState = (roomState: RoomState): void => {
    sonarScanView?.setRoomState(roomState);
  };

  const setRangeUpdateCallback = (callback: (() => void) | null): void => {
    sonarRangeUpdateCallback = callback;
  };

  const getRangeMeters = (): number => sonarRangeMeters;

  const capturePingHitsForNavigation = (snapshot: GameSnapshot, scanType: SonarScanType = sonarScanType): SonarPingHit[] => {
    if (sonarScanView) {
      sonarScanView.capture(snapshot, scanType);
      return sonarScanView.getLastCapturedPingHits();
    }

    if (!controllerState.lastRoomState) {
      return [];
    }

    const scratchViewport = document.createElement('div');
    scratchViewport.style.position = 'fixed';
    scratchViewport.style.left = '-9999px';
    scratchViewport.style.top = '-9999px';
    scratchViewport.style.width = '1px';
    scratchViewport.style.height = '1px';
    scratchViewport.style.overflow = 'hidden';
    scratchViewport.style.pointerEvents = 'none';
    document.body.append(scratchViewport);

    const scratchSonarView = new SonarScanView(scratchViewport, getGraphicsQuality());
    try {
      scratchSonarView.setRoomState(controllerState.lastRoomState);
      scratchSonarView.capture(snapshot, scanType);
      return scratchSonarView.getLastCapturedPingHits();
    } finally {
      scratchSonarView.dispose();
      scratchViewport.remove();
    }
  };

  const getScanType = (): SonarScanType => sonarScanType;

  return {
    render,
    updateDepthDial,
    setRoomState,
    updateBatteryWarning,
    setRangeUpdateCallback,
    incrementRange,
    decrementRange,
    getRangeMeters,
    getScanType,
    capturePingHitsForNavigation
  };
};
